import { signupSource } from "@/lib/referral";
import { useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const detailText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return "Enter a valid email address.";
  return fallback;
};

export default function EmailCodeLogin() {
  const { setUser, setIsAuthenticated } = useAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const requestCode = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(""); setInfo("");
    try {
      await api.post("/auth/email/request-code", { email: email.trim() });
      setStep("code");
      setInfo(`We sent a 6-digit code to ${email.trim()}.`);
    } catch (err) {
      setError(detailText(err, "Couldn't send the code. Try again."));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/email/verify", { email: email.trim(), code, source: signupSource() });
      const { data } = await api.get("/auth/me");
      setUser(data);
      setIsAuthenticated(true);
    } catch (err) {
      setError(detailText(err, "That code didn't work. Try again."));
      setBusy(false);
    }
  };

  if (step === "email") {
    return (
      <form onSubmit={requestCode} className="space-y-2" data-testid="email-login-form">
        <Input type="email" required autoComplete="email" inputMode="email" placeholder="you@icloud.com"
          value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="email-login-input" />
        <Button type="submit" variant="outline" disabled={busy || !email.trim()} className="h-11 w-full text-base font-semibold" data-testid="email-login-send-code">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
          Email me a sign-in code
        </Button>
        {error ? <p className="text-xs text-rose-600" data-testid="email-login-error">{error}</p> : null}
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="space-y-2" data-testid="email-code-form">
      {info ? <p className="text-xs text-slate-600" data-testid="email-login-info">{info}</p> : null}
      <Input autoFocus required inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456"
        value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        className="h-12 text-center font-mono text-2xl tracking-[0.5em]" data-testid="email-code-input" />
      <Button type="submit" disabled={busy || code.length !== 6} className="h-11 w-full bg-[#0053DA] text-base font-semibold hover:bg-[#0041B0]" data-testid="email-code-verify">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Sign in
      </Button>
      {error ? <p className="text-xs text-rose-600" data-testid="email-login-error">{error}</p> : null}
      <div className="flex justify-between text-xs">
        <button type="button" onClick={() => { setStep("email"); setCode(""); setError(""); }} className="text-slate-500 underline" data-testid="email-change-btn">Use a different email</button>
        <button type="button" onClick={requestCode} disabled={busy} className="text-[#0053DA] underline" data-testid="email-resend-btn">Resend code</button>
      </div>
    </form>
  );
}
